import i18next from "i18next";
import { SimpleAlert } from "../components/Alerts/Alerts";
import { PopupBlockedGuideAlert } from "../components/Alerts/PopupBlockedGuideAlert";
import {
  createMetaLoginMonitor,
  getIsMetaSdkLoginReady,
} from "../components/Containers/MetaSDKLoader";
import { setShowBackdrop } from "./ui";
import {
  ERROR,
  INTEGRATION_INFORMATION_NOT_FOUND,
  PLATFORMS_CONNECTED_INSTAGRAM_FAILED,
  PLATFORMS_CONNECTED_FACEBOOK_SDK_UNAVAILABLE,
  PLATFORMS_CONNECTED_META_BLOCKED_STEP_ALLOW_POPUPS,
  PLATFORMS_CONNECTED_META_BLOCKED_STEP_DISABLE_BLOCKER,
  PLATFORMS_CONNECTED_META_BLOCKED_STEP_RETRY,
  PLATFORMS_CONNECTED_META_BLOCKED_TITLE,
  PLATFORMS_CONNECTED_META_POPUP_BLOCKED,
  WARNING,
} from "../i18n/keysTranslations";
import {
  ADS_INTEGRATION_ENTITIES,
  ALERT_ICON_TYPE_ERROR,
  ALERT_ICON_TYPE_WARNING,
  CANVA,
  FACEBOOK,
  GOOGLE,
  LINKEDIN,
  LINKEDIN_PUBLISH_AS,
  GOOGLE_DRIVE,
  META,
  OWNER_ROLE,
  THEME_ID_PROJECT,
  TIKTOK_BUSINESS,
  TIKTOK,
  YOUTUBE,
  ADVERTISERS_DEMO,
  EDITOR_ASSETS_LIST_ROOT,
  EDITOR_ASSET_FOLDER_CONTENTS_ROOT,
  IS_STAGING,
} from "../utils/constants";
import { mergeDemoScheduledContents } from "../utils/organicContentsDemo";
import { SEAT_LIMIT_REACHED_CODE } from "../utils/extraSeats";
import {
  getPathDatabaseByRole,
  renewToken,
  reauthenticateWithCustomToken,
} from "./auth";
import axios from "../lib/axios";
import types from "../types";
import {
  get,
  limitToFirst,
  limitToLast,
  orderByChild,
  push,
  query,
  ref,
  remove,
  set,
  startAfter,
  startAt,
  endAt,
  endBefore,
  equalTo,
  update,
} from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { auth, db } from "../lib/firebase";
import { getUniqueID } from "../utils/numbers";
import { LaunchError, CREATIVE_JOB_TYPES } from "../utils/errors";
import { addNotification, updateNotification } from "./alerts";
import { ALERTS_ASYNC_STATUS } from "../utils/constants";
import _ from "lodash";
import {
  downloadContent,
  encryptPassword,
  getLanguageFormatted,
  hashStringToID,
} from "../utils/string";
import { getTotalMetricsCampaigns } from "./adsCampaigns";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import {
  downloadCarouselZip,
  downloadUrlsAsZip,
} from "../utils/downloadCarouselZip";
import { createCreativeJobAndWaitForResponse } from "./jobs";
import { promoteCreativeJobNotification } from "./creativeJobs";
import {
  getCreativeJobAlertMessageKey,
  mapJobResultToGenerationData,
} from "../utils/creativeJobsNotifications";
import {
  CREATIVE_ALERT_MODAL_TYPE,
  CREATIVE_ALERT_TYPE,
} from "../utils/creativeJobAlertConfig";
import { CREATIVE_JOB_STATUS } from "../utils/creativeJobStatus";
import { formatCompetitorAdsItemsForGallery } from "../services/competitorAdsGallery";
import { persistDataUrlToStorage } from "../services/persistDataUrlToStorage";
import { stripAssetFileExtension } from "../utils/assetGalleryUtils";
import { startClearAdvertiserDefaultPlatformValuesIfMatchingIntegration } from "./platformDefaults";
import { limitReferenceImageUrls } from "../utils/createWithIAReferenceImages";

const getRedirectUrl = () => {
  if (typeof window === "undefined") return undefined;
  return `${window.location.origin}/`;
};

export const startGetTotalMetricsUser = () => async (dispatch) => {
  try {
    const paths = dispatch(getPathDatabaseByRole());
    const dbRef = ref(db, `${paths.pathBase}/totalMetrics`);
    const snapshot = await get(dbRef);

    const totalMetrics = snapshot.val() || {};
    dispatch(getTotalMetricsCampaigns(totalMetrics));

    return totalMetrics;
  } catch (error) {
    dispatch(LaunchError(error));
    return false;
  }
};

export const startGetHasAddonSpendFee = () => async (dispatch) => {
  try {
    const { agencyID } = dispatch(getPathDatabaseByRole());
    if (!agencyID) {
      return false;
    }
    const dbRef = ref(db, `advertisers/${agencyID}/hasAddonSpendFee`);
    const snapshot = await get(dbRef);
    return snapshot.val();
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetHasAddonSpendFee",
      })
    );
    return false;
  }
};

export const startGetIntegrations = () => async (dispatch) => {
  try {
    const paths = dispatch(getPathDatabaseByRole());
    const dbRef = ref(db, paths.pathIntegrations);
    const snapshot = await get(dbRef);
    const integrationsPlatforms = [
      FACEBOOK,
      TIKTOK_BUSINESS,
      GOOGLE,
      CANVA,
      TIKTOK,
      YOUTUBE,
    ];

    if (snapshot.exists()) {
      const integrations = snapshot.val();
      dispatch(getIntegrations(integrations));
      const integrationsPlatformsFiltered = integrationsPlatforms.filter(
        (platform) => !integrations[platform]
      );
      dispatch(
        startCallBootIntegrationFunctions({
          platforms: integrationsPlatformsFiltered,
        })
      );
      return integrations;
    }
    dispatch(getIntegrations({}));
    dispatch(
      startCallBootIntegrationFunctions({
        platforms: integrationsPlatforms,
      })
    );
    return {};
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetIntegrations",
      })
    );
    return false;
  }
};

const buildFacebookSyncPatchFromResponse = ({
  integrationID,
  entity,
  responseData,
}) => {
  if (responseData == null || integrationID == null) {
    return null;
  }
  if (typeof responseData !== "object" || Array.isArray(responseData)) {
    return null;
  }

  const { AD_ACCOUNTS, PAGES } = ADS_INTEGRATION_ENTITIES;

  if (entity === AD_ACCOUNTS) {
    const businessAccounts =
      responseData.businessAccounts != null
        ? responseData.businessAccounts
        : responseData;
    if (
      typeof businessAccounts !== "object" ||
      Array.isArray(businessAccounts)
    ) {
      return null;
    }
    return { integrationID, businessAccounts };
  }

  if (entity === PAGES) {
    const pages =
      responseData.pages != null ? responseData.pages : responseData;
    if (typeof pages !== "object" || Array.isArray(pages)) {
      return null;
    }
    return { integrationID, pages };
  }

  return null;
};

const buildLinkedInSyncPatchFromResponse = ({
  integrationID,
  responseData,
}) => {
  if (responseData == null || integrationID == null) {
    return null;
  }

  const advertisers = responseData.advertisers;
  const pages = responseData.pages;

  const hasAdvertisers =
    advertisers != null &&
    typeof advertisers === "object" &&
    !Array.isArray(advertisers);
  const hasPages =
    pages != null && typeof pages === "object" && !Array.isArray(pages);

  if (!hasAdvertisers && !hasPages) {
    if (typeof responseData === "object" && !Array.isArray(responseData)) {
      return { integrationID, advertisers: responseData };
    }
    return null;
  }

  return {
    integrationID,
    ...(hasAdvertisers ? { advertisers } : {}),
    ...(hasPages ? { pages } : {}),
  };
};

const setFacebookIntegrationBusinessAccounts = (payload) => ({
  type: types.SET_FACEBOOK_INTEGRATION_BUSINESS_ACCOUNTS,
  payload,
});

const setFacebookIntegrationPages = (payload) => ({
  type: types.SET_FACEBOOK_INTEGRATION_PAGES,
  payload,
});

const setLinkedInIntegrationAdvertisers = (payload) => ({
  type: types.SET_LINKEDIN_INTEGRATION_ADVERTISERS,
  payload,
});

const setLinkedInIntegrationPages = (payload) => ({
  type: types.SET_LINKEDIN_INTEGRATION_PAGES,
  payload,
});

export const startSyncAdsAccountsEntities =
  ({ integrationID, platform, entity }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      if (ADVERTISERS_DEMO.includes(userID)) {
        return {
          ok: true,
          data: {
            data: [],
          },
        };
      }
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getAdsAccountsEntities")}/getAdsAccountsEntities`,
        data: {
          integrationID,
          platform,
          entity,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!data?.ok) {
        return {
          ok: false,
          message: data?.message,
          code: data?.code,
          syncPatch: null,
        };
      }

      let syncPatch = null;

      if (platform === META) {
        syncPatch = buildFacebookSyncPatchFromResponse({
          integrationID,
          entity,
          responseData: data.data,
        });

        if (entity === ADS_INTEGRATION_ENTITIES.AD_ACCOUNTS && syncPatch) {
          dispatch(setFacebookIntegrationBusinessAccounts(syncPatch));
        }

        if (entity === ADS_INTEGRATION_ENTITIES.PAGES && syncPatch) {
          dispatch(setFacebookIntegrationPages(syncPatch));
        }
      }

      if (platform === LINKEDIN) {
        syncPatch = buildLinkedInSyncPatchFromResponse({
          integrationID,
          responseData: data.data,
        });

        if (syncPatch?.advertisers) {
          dispatch(
            setLinkedInIntegrationAdvertisers({
              integrationID,
              advertisers: syncPatch.advertisers,
            })
          );
        }

        if (syncPatch?.pages) {
          dispatch(
            setLinkedInIntegrationPages({
              integrationID,
              pages: syncPatch.pages,
            })
          );
        }
      }

      return { ok: true, data: data.data, syncPatch };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [INTEGRATION_INFORMATION_NOT_FOUND], {
          functionName: "startSyncAdsAccountsEntities",
          context: { integrationID, platform, entity },
          tags: { action: "getAdsAccountsEntities" },
          level: "error",
        })
      );
      return { ok: false, syncPatch: null };
    }
  };

const getIntegrations = (data) => ({
  type: types.GET_INTEGRATIONS,
  payload: data,
});
const startCallBootIntegrationFunctions = () => async (dispatch) => {
  try {
    const promises = [];
    const token = await dispatch(renewToken());
    const role = dispatch(getPathDatabaseByRole())?.role;
    if (role === OWNER_ROLE || IS_STAGING) {
      return true;
    }

    // platforms.forEach((platform) => {
    // if (platform === FACEBOOK) {
    //   promises.push(
    //     axios({
    //       method: "post",
    //       url: `${FUNCTIONS_URL}/saveTokenFacebook`,
    //       data: {
    //         coldBoot: true,
    //       },
    //       headers: {
    //         Authorization: `Bearer ${token}`,
    //       },
    //     })
    //   );
    // }
    // if (platform === TIKTOK_BUSINESS) {
    promises.push(
      axios({
        method: "post",
        url: `${getCloudFunctionUrl("connectTiktokBusiness")}/connectTiktokBusiness`,
        data: {
          coldBoot: true,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    // promises.push(
    //   axios({
    //     method: "post",
    //     url: `${FUNCTIONS_URL}/getTokenTiktokBusiness`,
    //     data: {
    //       coldBoot: true,
    //     },
    //     headers: {
    //       Authorization: `Bearer ${token}`,
    //     },
    //   })
    // );
    // }
    // if (platform === GOOGLE) {
    promises.push(
      axios({
        method: "post",
        url: `${getCloudFunctionUrl("connectGoogleAds")}/connectGoogleAds`,
        data: {
          coldBoot: true,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    // promises.push(
    //   axios({
    //     method: "post",
    //     url: `${FUNCTIONS_URL}/getTokenGoogleAds`,
    //     data: {
    //       coldBoot: true,
    //     },
    //     headers: {
    //       Authorization: `Bearer ${token}`,
    //     },
    //   })
    // );
    // }

    // if (platform === CANVA) {
    //   promises.push(
    //     axios({
    //       method: "post",
    //       url: `${FUNCTIONS_URL}/connectCanva`,
    //       data: {
    //         coldBoot: true,
    //       },
    //       headers: {
    //         Authorization: `Bearer ${token}`,
    //       },
    //     })
    //   );
    //   promises.push(
    //     axios({
    //       method: "post",
    //       url: `${FUNCTIONS_URL}/getTokenCanva`,
    //       data: {
    //         coldBoot: true,
    //       },
    //       headers: {
    //         Authorization: `Bearer ${token}`,
    //       },
    //     })
    //   );
    // }
    // if (platform === YOUTUBE) {
    promises.push(
      axios({
        method: "post",
        url: `${getCloudFunctionUrl("connectYouTube")}/connectYouTube`,
        data: {
          coldBoot: true,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    // promises.push(
    //   axios({
    //     method: "post",
    //     url: `${FUNCTIONS_URL}/getTokenYouTube`,
    //     data: {
    //       coldBoot: true,
    //     },
    //     headers: {
    //       Authorization: `Bearer ${token}`,
    //     },
    //   })
    // );
    // }
    // });
    // if (platform === LINKEDIN) {
    promises.push(
      axios({
        method: "post",
        url: `${getCloudFunctionUrl("connectLinkedInAds")}/connectLinkedInAds`,
        data: {
          coldBoot: true,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    promises.push(
      axios({
        method: "post",
        url: `${getCloudFunctionUrl("updateCampaignEntityStatus")}/updateCampaignEntityStatus`,
        data: {
          coldBoot: true,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    promises.push(
      axios({
        method: "post",
        url: `${getCloudFunctionUrl("pausePublishingAutomation")}/pausePublishingAutomation`,
        data: {
          coldBoot: true,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    promises.push(
      axios({
        method: "post",
        url: `${getCloudFunctionUrl("resumePublishingAutomation")}/resumePublishingAutomation`,
        data: {
          coldBoot: true,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    // promises.push(
    //   axios({
    //     method: "post",
    //     url: `${FUNCTIONS_URL}/getTokenLinkedInAds`,
    //     data: {
    //       coldBoot: true,
    //     },
    //     headers: {
    //       Authorization: `Bearer ${token}`,
    //     },
    //   })
    // );
    // }
    // });

    // promises.push(
    //   axios({
    //     method: "post",
    //     url: `${FUNCTIONS_URL}/generateAwsDownloadUrl`,
    //     data: {
    //       coldBoot: true,
    //     },
    //     headers: {
    //       Authorization: `Bearer ${token}`,
    //     },
    //   }),
    //   axios({
    //     method: "post",
    //     url: `${FUNCTIONS_URL}/updateCampaignEntityStatus`,
    //     data: {
    //       coldBoot: true,
    //     },
    //     headers: {
    //       Authorization: `Bearer ${token}`,
    //     },
    //   })
    // );

    await Promise.all(promises);

    return true;
  } catch (error) {
    const response = dispatch(
      LaunchError(error, false, [], {
        functionName: "startCallBootIntegrationFunctions",
      })
    );
    console.log(response);
    return false;
  }
};
export const startGetAdvertisersByAgency = () => async (dispatch) => {
  try {
    const { pathAdvertisersByAgency, userID } = dispatch(
      getPathDatabaseByRole()
    );

    if (!userID) {
      return { ok: false };
    }

    const dbRef = ref(db, pathAdvertisersByAgency);
    const snapshot = await get(dbRef);
    if (snapshot.exists()) {
      const advertisersByAgency = snapshot.val();
      return { ok: true, data: advertisersByAgency };
    }
    return { ok: false };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetAdvertisersByAgency",
      })
    );
    return { ok: false };
  }
};

const normalizeAdvertisersByAgency = (raw) => {
  if (Array.isArray(raw)) {
    return raw.filter(Boolean);
  }
  if (raw && typeof raw === "object") {
    return Object.keys(raw);
  }
  return [];
};

export const startGetSuperAdminAgencyGroups =
  (agenciesAvailable = []) =>
  async (dispatch) => {
    try {
      if (!Array.isArray(agenciesAvailable) || agenciesAvailable.length === 0) {
        return { ok: false, data: [] };
      }

      const agencyGroups = await Promise.all(
        agenciesAvailable.map(async (agencyId) => {
          const [advertisersSnapshot, agencySnapshot] = await Promise.all([
            get(ref(db, `advertisersByAgency/${agencyId}`)),
            get(ref(db, `agencies/${agencyId}/name`)),
          ]);

          return {
            agencyId,
            agencyName: agencySnapshot.val() || agencyId,
            advertiserIDs: normalizeAdvertisersByAgency(
              advertisersSnapshot.val()
            ),
          };
        })
      );

      return { ok: true, data: agencyGroups };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetSuperAdminAgencyGroups",
        })
      );
      return { ok: false, data: [] };
    }
  };

export const startGetBrandsUsageAllocationData =
  (brandIDs = []) =>
  async (dispatch) => {
    try {
      if (!Array.isArray(brandIDs) || brandIDs.length === 0) {
        return { ok: true, data: {} };
      }

      const results = await Promise.all(
        brandIDs.map(async (brandID) => {
          const [allocationSnapshot, consumedSnapshot] = await Promise.all([
            get(ref(db, `advertisers/${brandID}/usageAllocation`)),
            get(ref(db, `advertisers/${brandID}/usageConsumed`)),
          ]);

          return {
            brandID,
            allocation: allocationSnapshot.val() || {},
            consumed: consumedSnapshot.val() || {},
          };
        })
      );

      const usageDataByBrand = {};
      for (const result of results) {
        usageDataByBrand[result.brandID] = {
          allocation: result.allocation,
          consumed: result.consumed,
        };
      }

      return { ok: true, data: usageDataByBrand };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetBrandsUsageAllocationData",
          context: { brandIDs },
        })
      );
      return { ok: false };
    }
  };

export const startSetBrandUsageAllocations =
  (brands = []) =>
  async (dispatch) => {
    try {
      const user = auth?.currentUser;
      if (!user) {
        return {
          ok: false,
          message: "User not authenticated",
          code: "UNAUTHORIZED",
        };
      }

      const token = await user.getIdToken();
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "setBrandUsageAllocations"
        )}/setBrandUsageAllocations`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          brands,
        },
      });

      if (data?.ok) {
        return {
          ok: true,
          data: data.data,
        };
      }

      return {
        ok: false,
        message: data?.message || "Failed to set brand usage allocations",
        code: data?.code,
      };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startSetBrandUsageAllocations",
          context: { brandCount: brands.length },
          tags: { operation: "set_brand_usage_allocations_batch" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

export const startGetLastSelectedIntegrationData = () => async (dispatch) => {
  try {
    const paths = dispatch(getPathDatabaseByRole());
    const dbRef = ref(db, paths.pathLastSelectedIntegrationData);
    const snapshot = await get(dbRef);
    if (snapshot.exists()) {
      const integrations = snapshot.val();
      dispatch(getLastSelectedIntegrationData(integrations));
      return integrations;
    }
    dispatch(getLastSelectedIntegrationData({}));
    return {};
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetLastSelectedIntegrationData",
      })
    );
    return false;
  }
};
const getLastSelectedIntegrationData = (data) => ({
  type: types.GET_LAST_SELECTED_INTEGRATION_DATA,
  payload: data,
});
export const startGetBusinessName = () => async (dispatch) => {
  try {
    const paths = dispatch(getPathDatabaseByRole());
    const dbRef = ref(db, `${paths.pathBase}/businessName`);
    const snapshot = await get(dbRef);
    if (snapshot.exists()) {
      const businessName = snapshot.val();
      dispatch(getBusinessName(businessName));
      return businessName;
    }
    return {};
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetBusinessName",
      })
    );
    return false;
  }
};
const getBusinessName = (data) => ({
  type: types.GET_BUSINESS_NAME,
  payload: data,
});

const setUserStorage = (data) => ({
  type: types.SET_USER_STORAGE,
  payload: data,
});

export const startGetSubtitlesConfiguration = () => async (dispatch) => {
  try {
    const paths = dispatch(getPathDatabaseByRole());
    const dbRef = ref(db, `${paths.pathBase}/subtitlesConfiguration`);
    const snapshot = await get(dbRef);
    if (snapshot.exists()) {
      return snapshot.val();
    }
    return null;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetSubtitlesConfiguration",
      })
    );
    return null;
  }
};

export const startSaveSubtitlesConfiguration =
  (configuration) => async (dispatch) => {
    try {
      const paths = dispatch(getPathDatabaseByRole());
      const dbRef = ref(db, `${paths.pathBase}/subtitlesConfiguration`);
      await set(dbRef, configuration);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveSubtitlesConfiguration",
        })
      );
      return false;
    }
  };

export const startGetAdsUser = (userID) => async (dispatch, getState) => {
  try {
    const currentUsers = getState().user.users || {};
    if (currentUsers?.[userID]) return currentUsers?.[userID];
    const paths = dispatch(getPathDatabaseByRole());
    const dbRef = ref(db, `${paths.pathUsers}/${userID}`);
    const snapshot = await get(dbRef);
    if (snapshot.exists()) {
      const adsUser = snapshot.val();
      dispatch(
        getAdsUsers({
          [userID]: adsUser,
        })
      );
      return adsUser;
    }
    return false;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetAdsUser",
      })
    );
    return false;
  }
};
export const startGetAdsUsers =
  ({ onCallbackLoadingFinish = () => {} } = {}) =>
  async (dispatch) => {
    try {
      const paths = dispatch(getPathDatabaseByRole());
      const dbRef = ref(db, `${paths.pathUsers}`);
      const snapshot = await get(dbRef);
      if (snapshot.exists()) {
        const adsUsers = snapshot.val();
        dispatch(getAdsUsers(adsUsers));
        onCallbackLoadingFinish();
        return adsUsers;
      }
      onCallbackLoadingFinish();
      return {};
    } catch (error) {
      console.log(error);
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetAdsUsers",
        })
      );
      onCallbackLoadingFinish();
      return false;
    }
  };
const getAdsUsers = (data) => ({
  type: types.GET_ADS_USERS,
  payload: data,
});

export const startSetLastSelectedIntegrationDataByPlatform =
  ({ platform, integrationID, adAccountID }) =>
  async (dispatch) => {
    try {
      const paths = dispatch(getPathDatabaseByRole());
      const path = `${paths.pathLastSelectedIntegrationData}/${platform}`;

      const dbRef = ref(db);

      const updates = {};

      updates[`${path}/integrationID`] = integrationID || null;
      updates[`${path}/adAccountID`] = adAccountID || null;

      await update(dbRef, updates);

      dispatch(
        setLastSelectedIntegrationDataByPlatform({
          platform,
          data: {
            integrationID,
            adAccountID: adAccountID || null,
          },
        })
      );

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSetLastSelectedIntegrationDataByPlatform",
        })
      );
      return false;
    }
  };
const setLastSelectedIntegrationDataByPlatform = (data) => ({
  type: types.SET_LAST_SELECTED_INTEGRATION_DATA_BY_PLATFORM,
  payload: data,
});

export const startConnectFacebook =
  ({
    onChangeLoadingAuth = () => {},
    onChangeLoadingConnect = () => {},
    showGlobalLoading = false,
    onCallbackConnected = () => {},
  }) =>
  async (dispatch) => {
    const resetLoading = () => {
      onChangeLoadingAuth(false);
      onChangeLoadingConnect(false);
      if (showGlobalLoading) dispatch(setShowBackdrop(false));
    };

    const showMetaBlockedAlert = ({ mode }) => {
      const isPopupFailure = mode.startsWith("popup_");

      SimpleAlert({
        title: i18next.t(PLATFORMS_CONNECTED_META_BLOCKED_TITLE),
        text: i18next.t(
          isPopupFailure
            ? PLATFORMS_CONNECTED_META_POPUP_BLOCKED
            : PLATFORMS_CONNECTED_FACEBOOK_SDK_UNAVAILABLE
        ),
        icon: ALERT_ICON_TYPE_WARNING,
        hasList: true,
        list: [
          i18next.t(PLATFORMS_CONNECTED_META_BLOCKED_STEP_DISABLE_BLOCKER),
          i18next.t(PLATFORMS_CONNECTED_META_BLOCKED_STEP_ALLOW_POPUPS),
          i18next.t(PLATFORMS_CONNECTED_META_BLOCKED_STEP_RETRY),
        ],
      });

      dispatch(
        LaunchError(new Error(`Meta connect failed: ${mode}`), false, [], {
          functionName: "startConnectFacebook",
          level: "warning",
          tags: { metaConnectFailure: mode },
        })
      );
    };

    try {
      onChangeLoadingAuth(true);
      if (showGlobalLoading) dispatch(setShowBackdrop(true));

      if (!getIsMetaSdkLoginReady()) {
        showMetaBlockedAlert({ mode: "sdk_unavailable" });
        resetLoading();
        return false;
      }

      const monitor = createMetaLoginMonitor({
        onCallbackFailed: (mode) => {
          showMetaBlockedAlert({ mode });
          resetLoading();
        },
        onCallbackCancelled: resetLoading,
      });

      monitor.runLogin(() => {
        window.FB.login(
          (response) => {
            if (!monitor.onResponse(response)) return;

            if (response.status !== "connected" || !response.authResponse) {
              SimpleAlert({
                title: i18next.t(ERROR),
                text: i18next.t(PLATFORMS_CONNECTED_INSTAGRAM_FAILED),
                icon: ALERT_ICON_TYPE_ERROR,
              });
              resetLoading();
              return;
            }

            const { accessToken, userID } = response.authResponse;
            onChangeLoadingAuth(false);
            dispatch(
              startSaveTokenFacebook({
                data: {
                  id: userID,
                  accessToken,
                  name: "",
                },
                showGlobalLoading,
                onChangeLoading: onChangeLoadingConnect,
                onCallbackConnected,
              })
            );
          },
          {
            config_id: import.meta.env.VITE_FACEBOOK_CONFIG_ID,
          }
        );
      });
    } catch (error) {
      resetLoading();
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startConnectFacebook",
        })
      );
      return false;
    }
  };
export const startSaveTokenFacebook =
  ({
    data,
    showGlobalLoading = false,
    onChangeLoading = () => {},
    onCallbackConnected = () => {},
  }) =>
  async (dispatch, getState) => {
    try {
      const token = await dispatch(renewToken());

      const metaIntegrations = getState()?.user?.integrations?.facebook;
      onChangeLoading(true);
      if (showGlobalLoading) dispatch(setShowBackdrop(true));

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("saveTokenFacebook")}/saveTokenFacebook`,
        data,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      onChangeLoading(false);
      if (showGlobalLoading) dispatch(setShowBackdrop(false));

      if (!result.ok) return false;

      if (_.isEmpty(metaIntegrations)) {
        dispatch(
          startSetLastSelectedIntegrationDataByPlatform({
            platform: META,
            integrationID: result?.data?.id,
            adAccountID: Object.keys(result?.data?.businessAccounts || {})[0],
          })
        );
      }

      dispatch(saveFacebookIntegration(result.data));
      onCallbackConnected(result.data);
      return true;
    } catch (error) {
      onChangeLoading(false);
      if (showGlobalLoading) dispatch(setShowBackdrop(false));
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveTokenFacebook",
        })
      );
      return false;
    }
  };
const saveFacebookIntegration = (data) => ({
  type: types.GET_FACEBOOK_INTEGRATION,
  payload: data,
});
export const startRevokeFacebookAccount =
  ({
    showGlobalLoading = false,
    onChangeLoading = () => {},
    onCallbackRevoked = () => {},
    id,
  }) =>
  async (dispatch) => {
    try {
      onChangeLoading(true);
      if (showGlobalLoading) dispatch(setShowBackdrop(true));

      window.FB.getLoginStatus(async (response) => {
        if (response.session) {
          window.FB.logout(
            dispatch(
              startRevokeFacebookAccount({
                showGlobalLoading,
                onChangeLoading,
              })
            )
          );
        } else {
          const token = await dispatch(renewToken());
          const { data: result } = await axios({
            method: "post",
            url: `${getCloudFunctionUrl("revokeFacebookAccount")}/revokeFacebookAccount`,
            headers: {
              Authorization: `Bearer ${token}`,
            },
            data: { id },
          });

          onChangeLoading(false);
          if (showGlobalLoading) dispatch(setShowBackdrop(false));

          if (result.ok) {
            dispatch(
              revokeFacebookIntegration({
                id,
              })
            );
            await dispatch(
              startClearAdvertiserDefaultPlatformValuesIfMatchingIntegration({
                platform: META,
                integrationID: id,
              })
            );
            onCallbackRevoked();
            return true;
          }
          return false;
        }
      });
    } catch (error) {
      onChangeLoading(false);
      if (showGlobalLoading) dispatch(setShowBackdrop(false));
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startRevokeFacebookAccount",
        })
      );
      return false;
    }
  };
const revokeFacebookIntegration = (data) => ({
  type: types.REVOKE_FACEBOOK_INTEGRATION,
  payload: data,
});

export const filterContentsNotInContentList =
  (contents) => async (dispatch) => {
    if (!Array.isArray(contents) || contents.length === 0) {
      return [];
    }

    const { userID } = dispatch(getPathDatabaseByRole());
    if (!userID) {
      return contents;
    }

    const urls = [
      ...new Set(contents.map((item) => item?.url).filter(Boolean)),
    ];
    const existingUrls = new Set();

    await Promise.all(
      urls.map(async (url) => {
        try {
          const contentQuery = query(
            ref(db, `contentList/${userID}`),
            orderByChild("url"),
            equalTo(url)
          );
          const snapshot = await get(contentQuery);

          if (snapshot.exists()) {
            existingUrls.add(url);
          }
        } catch {
          // Index may be missing; skip dedupe for this URL.
        }
      })
    );

    return contents.filter((item) => !item?.url || !existingUrls.has(item.url));
  };

export const startAddContentsToList =
  (data, options = {}) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const { preserveOrder = false, listRoot = "contentList" } = options;
      const baseTime = Date.now();

      const updates = {};

      const returnData = {};

      data.forEach((item, index) => {
        const key = push(ref(db, `${listRoot}/${userID}`)).key;
        const contentFolderID =
          listRoot === "contentList" || listRoot === EDITOR_ASSETS_LIST_ROOT
            ? item?.folderID
            : null;
        const creationTime =
          preserveOrder && data.length > 1
            ? baseTime + (data.length - 1 - index)
            : Date.now();
        const newData = {
          ...item,
          id: key,
          creationTime,
          creationTimeNegative: -creationTime,
        };

        updates[`${listRoot}/${userID}/${key}`] = newData;
        if (contentFolderID) {
          const folderContentsRoot =
            listRoot === EDITOR_ASSETS_LIST_ROOT
              ? EDITOR_ASSET_FOLDER_CONTENTS_ROOT
              : "contentFolderContents";
          updates[`${folderContentsRoot}/${userID}/${contentFolderID}/${key}`] =
            true;
        }
        returnData[key] = newData;
      });

      await update(ref(db), updates);

      return returnData;
    } catch (error) {
      console.log(error);
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startAddContentsToList",
        })
      );
      return false;
    }
  };

export const startAnalyzeContent =
  ({ url, id }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("analyzeContent")}/analyzeContent`,
        data: { url, id },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = result.data;

      return { ok: true, data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          sentryMeta: { url, id },
          functionName: "startAnalyzeContent",
        })
      );
      return false;
    }
  };
export const startGetContentRecommendations =
  ({ platform, contents, objective, campaignContext }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const language = getLanguageFormatted(i18next.language);

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getContentRecommendations")}/getContentRecommendations`,
        data: { platform, contents, objective, campaignContext, language },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = result.data;

      return { ok: true, data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          sentryMeta: { platform, contents, objective, campaignContext },
          functionName: "startGetContentRecommendations",
        })
      );
      return false;
    }
  };

export const startGetSuggestedPublishSchedule =
  ({ description = "", platforms = [], urls = [], language, timezone }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const resolvedLanguage =
        language != null
          ? getLanguageFormatted(language)
          : getLanguageFormatted(i18next.language);

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getSuggestedPublishSchedule")}/getSuggestedPublishSchedule`,
        data: {
          description,
          platforms,
          urls,
          language: resolvedLanguage,
          timezone,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (result?.ok && result?.data) {
        return result.data;
      }

      return null;
    } catch (error) {
      console.error("Error fetching suggested publish schedule:", error);
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetSuggestedPublishSchedule",
        })
      );
      return null;
    }
  };

export const startMatchCreativeProductAsset =
  ({
    mediaType,
    campaignID,
    campaignContext,
    prompt,
    language,
    platform,
    isCarousel,
    videoType,
    imagesUrl,
    referenceAdUrl,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("matchCreativeProductAsset")}/matchCreativeProductAsset`,
        data: {
          mediaType,
          campaignID,
          campaignContext,
          prompt,
          language,
          platform,
          isCarousel,
          videoType,
          imagesUrl,
          referenceAdUrl,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!result?.ok) {
        return { ok: false };
      }

      if (!Array.isArray(result.data) || !result.data.length) {
        return { ok: false };
      }

      return { ok: true, data: result.data };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startMatchCreativeProductAsset",
          context: { mediaType, campaignID },
          tags: { action: "matchCreativeProductAsset" },
          level: "warning",
          isWarning: true,
        })
      );
      return { ok: false };
    }
  };

export const startValidateYoutubeVideos = (urls) => async (dispatch) => {
  try {
    const { data: result } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("validateYoutubeVideos")}/validateYoutubeVideos`,
      data: { videoUrls: urls },
    });

    return result;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        sentryMeta: { urls },
        functionName: "startValidateYoutubeVideos",
      })
    );
    return {
      ok: false,
    };
  }
};

const getFilterPaginationKey = (lastKey) =>
  typeof lastKey === "string" && lastKey.length > 0 ? lastKey : null;

const getTimePaginationCursor = (lastKey) =>
  lastKey?.value != null && typeof lastKey?.id === "string"
    ? { value: lastKey.value, id: lastKey.id }
    : null;

export const startGetGalleryContent =
  ({
    lastKey,
    onChangeLastKey = () => {},
    limit = 15,
    onLoadLastItem = () => {},
    filter = null,
    filterType = null,
    folderID = null,
    listRoot = "contentList",
  }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      const dbRef = ref(db, `${listRoot}/${userID}`);
      const queryFilter = folderID || filter;
      const queryFilterType = folderID ? "folderID" : filterType;
      let q;

      // If there are filters, use orderByChild with filterType and filter by filter value
      // Using limitToLast to get the most recent items first
      if (queryFilter !== null && queryFilterType !== null) {
        const base = query(dbRef, orderByChild(queryFilterType));
        const paginationKey = getFilterPaginationKey(lastKey);
        q = paginationKey
          ? query(
              base,
              startAt(queryFilter),
              endBefore(queryFilter, paginationKey),
              limitToLast(limit)
            )
          : query(
              base,
              startAt(queryFilter),
              endAt(queryFilter),
              limitToLast(limit)
            );
      }

      // No filters: paginate by creationTimeNegative. Cursor { value, id } avoids
      // skipping items when creationTimeNegative is duplicated across rows.
      if (queryFilter === null || queryFilterType === null) {
        const cursor = getTimePaginationCursor(lastKey);
        if (cursor == null) {
          q = query(
            dbRef,
            orderByChild("creationTimeNegative"),
            limitToFirst(limit)
          );
        } else {
          const { value, id } = cursor;
          q = query(
            dbRef,
            orderByChild("creationTimeNegative"),
            startAfter(value, id),
            limitToFirst(limit)
          );
        }
      }

      const snapshot = await get(q);

      if (!snapshot.exists()) {
        onLoadLastItem();
        return {};
      }

      const data = snapshot.val();
      // Build ordered array from snapshot to preserve query order (critical for cursor)
      const orderedItems = [];
      snapshot.forEach((child) => {
        orderedItems.push({ key: child.key, ...child.val() });
      });
      const values = orderedItems;
      const keys = orderedItems.map((i) => i.key);

      if (queryFilter !== null && queryFilterType !== null && keys.length > 0) {
        // For filters with limitToLast, use the first element's key as cursor for next page
        onChangeLastKey(keys[0]);
      } else if (values.length > 0) {
        // Cursor: last item in query order (oldest in batch). Use {value, id} for startAfter
        // to avoid skipping items with duplicate creationTimeNegative
        const lastItem = values[values.length - 1];
        onChangeLastKey({
          value: lastItem?.creationTimeNegative,
          id: lastItem?.key,
        });
      }

      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetGalleryContent",
        })
      );
      return false;
    }
  };

export const startAddAssetsToList = (data, options = {}) =>
  startAddContentsToList(
    (Array.isArray(data) ? data : []).map((item) => ({
      ...item,
      name: stripAssetFileExtension({ name: item?.name }) || undefined,
    })),
    {
      ...options,
      listRoot: EDITOR_ASSETS_LIST_ROOT,
    }
  );

export const startGetGalleryAssets = (params = {}) =>
  startGetGalleryContent({
    ...params,
    listRoot: EDITOR_ASSETS_LIST_ROOT,
  });

export const startRenameAsset =
  ({ contentID, name }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const trimmedName = stripAssetFileExtension({ name });

      if (!userID || !contentID || !trimmedName) {
        return { ok: false, reason: "invalidName" };
      }

      await update(ref(db), {
        [`${EDITOR_ASSETS_LIST_ROOT}/${userID}/${contentID}/name`]: trimmedName,
      });

      return {
        ok: true,
        data: {
          id: contentID,
          name: trimmedName,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startRenameAsset",
          context: { contentID, name },
        })
      );
      return { ok: false };
    }
  };

export const startDeleteAssetFromListAndStorage = ({ contentID }) =>
  startDeleteContentFromListAndStorage({
    contentID,
    listRoot: EDITOR_ASSETS_LIST_ROOT,
  });

export const startGetContentsFromList =
  ({
    lastKey,
    onChangeLastKey = () => {},
    limit = 15,
    onLoadLastItem = () => {},
  }) =>
  async (dispatch) => {
    try {
      let q;

      const { userID } = dispatch(getPathDatabaseByRole());

      q = lastKey
        ? query(
            ref(db, `contentList/${userID}`),
            orderByChild("creationTimeNegative"),
            startAfter(lastKey),
            limitToLast(limit)
          )
        : query(
            ref(db, `contentList/${userID}`),
            orderByChild("creationTimeNegative"),
            limitToLast(limit)
          );

      const snapshot = await get(q);

      if (!snapshot.exists()) {
        onLoadLastItem();
        return {};
      }

      const data = snapshot.val();
      const values = Object.values(data);

      onChangeLastKey(values[0].creationTimeNegative);

      return data;
    } catch (error) {
      onLoadLastItem(true);
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetContentsFromList",
        })
      );
      return false;
    }
  };
export const startGetAnalysisContentList =
  ({ id }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(db, `contentList/${userID}/${id}/analysis`);
      const snapshot = await get(dbRef);
      if (!snapshot.exists()) return { ok: false };
      return { ok: true, data: snapshot.val() };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          sentryMeta: id,
          functionName: "startGetAnalysisContentList",
        })
      );
      return { ok: false };
    }
  };

export const startGetContentItemById =
  ({ contentID }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!userID || !contentID) {
        return { ok: false };
      }

      const snapshot = await get(ref(db, `contentList/${userID}/${contentID}`));

      if (!snapshot.exists()) {
        return { ok: false };
      }

      const entry = snapshot.val();

      return {
        ok: true,
        data: {
          ...entry,
          id: contentID,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetContentItemById",
          context: { contentID },
        })
      );
      return { ok: false };
    }
  };

export const startGetContentItemsByIds =
  ({ contentIDs = [] }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!userID || !Array.isArray(contentIDs) || contentIDs.length === 0) {
        return { ok: false, items: [] };
      }

      const uniqueContentIDs = [
        ...new Set(contentIDs.filter(Boolean).map(String)),
      ];

      const fetchedEntries = await Promise.all(
        uniqueContentIDs.map(async (contentID) => {
          const snapshot = await get(
            ref(db, `contentList/${userID}/${contentID}`)
          );

          if (!snapshot.exists()) {
            return null;
          }

          return {
            ...snapshot.val(),
            id: contentID,
          };
        })
      );

      const itemsById = new Map(
        fetchedEntries.filter(Boolean).map((entry) => [entry.id, entry])
      );

      const items = contentIDs
        .map((contentID) => itemsById.get(String(contentID)))
        .filter(Boolean);

      return {
        ok: items.length > 0,
        items,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetContentItemsByIds",
          context: { contentIDs },
        })
      );
      return { ok: false, items: [] };
    }
  };

export const startUpdateUrlInContentList =
  ({ url, id }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(db, `contentList/${userID}/${id}/url`);
      const snapshot = await get(dbRef);
      if (!snapshot.exists()) return false;

      await set(dbRef, url);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUpdateUrlInContentList",
        })
      );
      return false;
    }
  };

export const startBumpContentInList =
  ({ id }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const contentRef = ref(db, `contentList/${userID}/${id}`);
      const snapshot = await get(contentRef);
      if (!snapshot.exists()) return false;

      const creationTime = Date.now();
      await update(contentRef, {
        creationTime,
        creationTimeNegative: -creationTime,
      });
      return { creationTime, creationTimeNegative: -creationTime };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startBumpContentInList",
        })
      );
      return false;
    }
  };

export const startBumpAndUpdateContentInList =
  ({ id, url, sourceUrl, subtitles, duration }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const contentRef = ref(db, `contentList/${userID}/${id}`);
      const snapshot = await get(contentRef);
      if (!snapshot.exists()) return false;

      const creationTime = Date.now();
      const updates = {
        creationTime,
        creationTimeNegative: -creationTime,
        originalVideoUrl: null,
      };
      if (url) {
        updates.url = url;
      }
      if (sourceUrl) {
        updates.sourceUrl = sourceUrl;
      }
      if (subtitles) {
        updates.subtitles = subtitles;
      }
      if (duration != null) {
        updates.duration = duration;
      }

      await update(contentRef, updates);
      return { creationTime, creationTimeNegative: -creationTime };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startBumpAndUpdateContentInList",
        })
      );
      return false;
    }
  };

export const startReplaceContentMediaInList =
  ({ contentID, url, urls, type }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const updates = {};

      if (Array.isArray(urls) && urls.length > 0) {
        updates[`contentList/${userID}/${contentID}/urls`] = urls;
        updates[`contentList/${userID}/${contentID}/url`] = urls[0];
        updates[`contentList/${userID}/${contentID}/type`] = type || "carousel";
      } else if (url) {
        updates[`contentList/${userID}/${contentID}/url`] = url;
        if (type) {
          updates[`contentList/${userID}/${contentID}/type`] = type;
        }
      } else {
        return false;
      }

      await update(ref(db), updates);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startReplaceContentMediaInList",
        })
      );
      return false;
    }
  };

export const startUpdateCarouselImageUrl =
  ({ contentID, carouselIndex, url }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      // En Firebase, los carruseles se guardan con 'urls' como array de strings
      const dbRef = ref(
        db,
        `contentList/${userID}/${contentID}/urls/${carouselIndex}`
      );
      const snapshot = await get(dbRef);
      if (!snapshot.exists()) return false;

      await set(dbRef, url);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUpdateCarouselImageUrl",
        })
      );
      return false;
    }
  };

export const startDeleteContentFromListAndStorage =
  ({ contentID, listRoot = "contentList" }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const dbPath = `${listRoot}/${userID}/${contentID}`;
      const dbRef = ref(db, dbPath);
      const snapshot = await get(dbRef);
      const folderID = snapshot.val()?.folderID;
      const updates = {
        [dbPath]: null,
      };

      if (folderID) {
        const folderContentsRoot =
          listRoot === EDITOR_ASSETS_LIST_ROOT
            ? EDITOR_ASSET_FOLDER_CONTENTS_ROOT
            : "contentFolderContents";
        updates[`${folderContentsRoot}/${userID}/${folderID}/${contentID}`] =
          null;
      }

      await update(ref(db), updates);

      return true;
    } catch (error) {
      console.log(error);
      dispatch(LaunchError(error));
      return false;
    }
  };

export const startGetScheduledContents = () => async (dispatch) => {
  try {
    const { userID } = dispatch(getPathDatabaseByRole());
    const dbRef = ref(db, `scheduledContents/${userID}`);
    const snapshot = await get(dbRef);

    const data = snapshot.exists() ? snapshot.val() : {};

    return mergeDemoScheduledContents(data, {
      advertiserID: userID,
      userID,
    });
  } catch (error) {
    console.error(
      "❌ [startGetScheduledContents] Error al consultar Firebase:",
      error
    );
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetScheduledContents",
      })
    );
    return false;
  }
};

export const startSaveAdvertiserTimezone =
  ({ timezone }) =>
  async (dispatch) => {
    try {
      const { pathBrandInformation } = dispatch(getPathDatabaseByRole());
      if (!pathBrandInformation) {
        return { ok: false };
      }

      await update(ref(db), {
        [`${pathBrandInformation}/timezone`]: timezone || "",
      });

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveAdvertiserTimezone",
        })
      );
      return { ok: false };
    }
  };

export const startListenerAdvertiserTimezone =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { pathBrandInformation } = dispatch(getPathDatabaseByRole());
      if (!pathBrandInformation) {
        onUpdatedDataCallback(null);
        return false;
      }

      const dbRef = ref(db, `${pathBrandInformation}/timezone`);

      onValue(dbRef, (snapshot) => {
        const value = snapshot.val();
        onUpdatedDataCallback(
          typeof value === "string" && value ? value : null
        );
      });

      onReferenceAvailable(dbRef);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startListenerAdvertiserTimezone",
        })
      );
      return false;
    }
  };

export const startListenerScheduledContents =
  (onDataChange = () => {}, onReferenceAvailable = () => {}) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      const dbRef = ref(db, `scheduledContents/${userID}`);

      onValue(dbRef, (snapshot) => {
        const data = snapshot.exists() ? snapshot.val() : {};

        onDataChange(
          mergeDemoScheduledContents(data, {
            advertiserID: userID,
            userID,
          })
        );
      });

      onReferenceAvailable(dbRef);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startListenerScheduledContents",
        })
      );
      return false;
    }
  };

export const startListenerAgencyStorage =
  (onReferenceAvailable = () => {}) =>
  async (dispatch) => {
    try {
      const { agencyID } = dispatch(getPathDatabaseByRole());

      if (!agencyID) {
        return false;
      }

      const dbRef = ref(db, `agencies/${agencyID}/storage`);

      onValue(dbRef, (snapshot) => {
        const value = snapshot.val() || {};

        dispatch(
          setUserStorage({
            usedBytes: value.usedBytes || 0,
            updatedDateTime: value.updatedDateTime || null,
            maxBytes: value.maxBytes,
          })
        );
      });

      onReferenceAvailable(dbRef);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startListenerAgencyStorage",
        })
      );
      return false;
    }
  };

export const startConnectCanva =
  ({ onChangeLoading, onChangePopupReference = () => {} }) =>
  async (dispatch) => {
    const width = 500;
    const height = 700;
    const popup = window.open(
      "about:blank",
      "Canva Auth",
      `width=${width},height=${height}`
    );

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      PopupBlockedGuideAlert();
      return false;
    }

    try {
      onChangeLoading(true);
      const token = await dispatch(renewToken());
      const response = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("connectCanva")}/connectCanva`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          redirectUrl: getRedirectUrl(),
        },
      });
      onChangeLoading(false);
      if (response.data.ok) {
        popup.location.href = response.data.url;
        onChangePopupReference(popup);
        return;
      }

      popup.close();
      return false;
    } catch (error) {
      onChangeLoading(false);
      popup.close();
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startConnectCanva",
        })
      );
      return false;
    }
  };

export const startGetTokenCanva = (data) => async (dispatch) => {
  const withAuth = data?.withAuth ?? false;
  try {
    let headers = {};
    if (withAuth) {
      const token = await dispatch(renewToken());
      headers = {
        Authorization: `Bearer ${token}`,
      };
    }
    const { data: result } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("getTokenCanva")}/getTokenCanva`,
      data: {
        ...data,
        redirectUrl: getRedirectUrl(),
      },
      headers,
    });

    if (result.ok) {
      if (withAuth) {
        dispatch(getCanvaIntegration(result.data));
      }
      return true;
    }
    return false;
  } catch (error) {
    dispatch(
      LaunchError(error, withAuth, [], {
        functionName: "startGetTokenCanva",
      })
    );
    return false;
  }
};
const getCanvaIntegration = (data) => ({
  type: types.GET_CANVA_INTEGRATION,
  payload: data,
});

export const startRevokeCanvaAccount =
  ({ integrationID } = {}) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("revokeCanva")}/revokeCanva`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { integrationID },
      });
      if (result.ok) {
        dispatch(revokeCanvaIntegration({ id: integrationID }));
        return true;
      }
      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startRevokeCanvaAccount",
        })
      );
      return false;
    }
  };
const revokeCanvaIntegration = (data) => ({
  type: types.REVOKE_CANVA_INTEGRATION,
  payload: data,
});

export const startCreateCanvaDesign =
  ({ width, height, imageUrl, coldBoot, integrationID } = {}) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("createCanvaDesign")}/createCanvaDesign`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          coldBoot,
          width,
          height,
          integrationID,
          ...(imageUrl && { imageUrl }),
        },
      });
      if (result.ok) {
        return result.data;
      }
      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCreateCanvaDesign",
        })
      );
      return false;
    }
  };
export const startGetCanvaDesign =
  ({ designID, integrationID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getCanvaDesign")}/getCanvaDesign`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          designID,
          integrationID,
        },
      });
      if (result.ok) {
        return result?.data;
      }
      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetCanvaDesign",
        })
      );
      return false;
    }
  };
export const startExportCanvaDesign =
  ({ designID, contentType, orientation, integrationID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("exportCanvaDesign")}/exportCanvaDesign`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          designID,
          contentType,
          orientation,
          integrationID,
        },
      });
      if (result.ok) {
        return result?.data;
      }
      return false;
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.log("Request aborted");
        return { ok: false, aborted: true };
      }
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startExportCanvaDesign",
        })
      );
      return false;
    }
  };

export const startGetCanvaFolders =
  ({ integrationID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getCanvaFolders")}/getCanvaFolders`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          integrationID,
        },
      });
      console.log("result", result);
      if (result.ok) {
        return {
          ok: true,
          data: result.data,
        };
      }
      return {
        ok: false,
        message: result.message,
        code: result.code,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetCanvaFolders",
        })
      );
      return { ok: false };
    }
  };

export const startConnectTiktokBusiness =
  ({ onChangeLoading, onChangePopupReference = () => {} }) =>
  async (dispatch) => {
    const width = 500;
    const height = 700;
    const popup = window.open(
      "about:blank",
      "TikTok Business Auth",
      `width=${width},height=${height}`
    );

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      PopupBlockedGuideAlert();
      return false;
    }

    try {
      onChangeLoading(true);
      const token = await dispatch(renewToken());
      const response = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("connectTiktokBusiness")}/connectTiktokBusiness`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          themeID: THEME_ID_PROJECT,
          redirectUrl: getRedirectUrl(),
        },
      });
      onChangeLoading(false);
      if (response.data.ok) {
        popup.location.href = response.data.url;
        onChangePopupReference(popup);
        return;
      }

      popup.close();
      return false;
    } catch (error) {
      onChangeLoading(false);
      popup.close();
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startConnectTiktokBusiness",
        })
      );
      return false;
    }
  };
export const startGetTokenTiktokBusiness =
  (data) => async (dispatch, getState) => {
    try {
      const token = await dispatch(renewToken());

      const tikTokBusinessIntegrations =
        getState()?.user?.integrations?.tiktokBusiness;

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getTokenTiktokBusiness")}/getTokenTiktokBusiness`,
        data: {
          ...data,
          redirectUrl: getRedirectUrl(),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!result.ok) return { ok: false };

      if (_.isEmpty(tikTokBusinessIntegrations)) {
        dispatch(
          startSetLastSelectedIntegrationDataByPlatform({
            platform: TIKTOK_BUSINESS,
            integrationID: result?.data?.id,
            adAccountID: Object.keys(result?.data?.advertisers || {})[0],
          })
        );
      }

      dispatch(getTiktokBusinessIntegration(result.data));
      return { ok: true, data: result.data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetTokenTiktokBusiness",
        })
      );

      return { ok: false };
    }
  };
const getTiktokBusinessIntegration = (data) => ({
  type: types.GET_TIKTOK_BUSINESS_INTEGRATION,
  payload: data,
});

const addTiktokBusinessAdvertiser = (data) => ({
  type: types.ADD_TIKTOK_BUSINESS_ADVERTISER,
  payload: data,
});

export const startGetBusinessCentersTikTokBusiness =
  ({ integrationID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getBusinessCentersTikTokBusiness")}/getBusinessCentersTikTokBusiness`,
        data: { integrationID },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data?.ok) {
        return { ok: false, message: data?.message, code: data?.code };
      }

      return { ok: true, data: data.data || [] };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetBusinessCentersTikTokBusiness",
          context: { integrationID },
          tags: { action: "getBusinessCentersTikTokBusiness" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startCreateAdvertiserTikTokBusiness =
  ({
    integrationID,
    bcID,
    name,
    company,
    industry,
    registeredArea,
    currency,
    timezone,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("createAdvertiserTikTokBusiness")}/createAdvertiserTikTokBusiness`,
        data: {
          integrationID,
          bcID,
          name,
          company,
          industry,
          registeredArea,
          currency,
          timezone,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data?.ok) {
        return { ok: false, message: data?.message, code: data?.code };
      }

      dispatch(
        addTiktokBusinessAdvertiser({
          integrationID,
          advertiser: data.data,
        })
      );

      return { ok: true, data: data.data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCreateAdvertiserTikTokBusiness",
          context: { integrationID, bcID, name, registeredArea, currency },
          tags: { action: "createAdvertiserTikTokBusiness" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startRevokeTiktokBusinessAccount =
  ({ id }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("revokeTiktokBusinessAccount")}/revokeTiktokBusinessAccount`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          id,
        },
      });
      if (result.ok) {
        dispatch(
          revokeTiktokBusinessIntegration({
            id,
          })
        );
        await dispatch(
          startClearAdvertiserDefaultPlatformValuesIfMatchingIntegration({
            platform: TIKTOK,
            integrationID: id,
          })
        );
        return true;
      }
      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startRevokeTiktokBusinessAccount",
        })
      );
      return false;
    }
  };
const revokeTiktokBusinessIntegration = (data) => ({
  type: types.REVOKE_TIKTOK_BUSINESS_INTEGRATION,
  payload: data,
});

export const startConnectTiktok =
  ({ onChangeLoading, onChangePopupReference = () => {} }) =>
  async (dispatch) => {
    const width = 500;
    const height = 700;
    const popup = window.open(
      "about:blank",
      "TikTok Auth",
      `width=${width},height=${height}`
    );

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      PopupBlockedGuideAlert();
      return false;
    }

    try {
      onChangeLoading?.(true);
      const token = await dispatch(renewToken());
      const response = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("connectTiktok")}/connectTiktok`,
        headers: { Authorization: `Bearer ${token}` },
        data: {
          themeID: THEME_ID_PROJECT,
          redirectUrl: getRedirectUrl(),
        },
      });
      onChangeLoading?.(false);
      if (!response.data?.ok) {
        popup.close();
        return false;
      }

      const url = response.data.url;
      if (!url) {
        console.error("No URL returned from connectTiktok endpoint");
        popup.close();
        return false;
      }

      popup.location.href = url;
      onChangePopupReference(popup);
      return true;
    } catch (error) {
      onChangeLoading?.(false);
      popup.close();
      dispatch(
        LaunchError(error, true, [], { functionName: "startConnectTiktok" })
      );
      return false;
    }
  };

export const startGetTokenTiktok = (data) => async (dispatch, getState) => {
  try {
    const token = await dispatch(renewToken());

    const currentIntegrations = getState()?.user?.integrations?.tiktok;

    const { data: result } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("getTokenTiktok")}/getTokenTiktok`,
      data: {
        ...data,
        redirectUrl: getRedirectUrl(),
      },
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!result?.ok) return { ok: false };

    if (_.isEmpty(currentIntegrations)) {
      dispatch(
        startSetLastSelectedIntegrationDataByPlatform({
          platform: TIKTOK,
          integrationID: result.data.id,
          adAccountID: Object.keys(result.data.advertisers || {})[0],
        })
      );
    }

    dispatch(getTiktokIntegration(result.data));
    return { ok: true, data: result.data };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], { functionName: "startGetTokenTiktok" })
    );
    return { ok: false };
  }
};

const getTiktokIntegration = (data) => ({
  type: types.GET_TIKTOK_INTEGRATION,
  payload: data,
});

export const startRevokeTiktokAccount =
  ({ id }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("revokeTiktokAccount")}/revokeTiktokAccount`,
        headers: { Authorization: `Bearer ${token}` },
        data: { id },
      });

      if (!result?.ok) return false;

      dispatch(revokeTiktokIntegration({ id }));
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startRevokeTiktokAccount",
        })
      );
      return false;
    }
  };

const revokeTiktokIntegration = (data) => ({
  type: types.REVOKE_TIKTOK_INTEGRATION,
  payload: data,
});

export const startConnectGoogleAds =
  ({ onChangeLoading, onChangePopupReference = () => {} }) =>
  async (dispatch) => {
    const width = 500;
    const height = 700;
    const popup = window.open(
      "about:blank",
      "Google Auth",
      `width=${width},height=${height}`
    );

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      PopupBlockedGuideAlert();
      return false;
    }

    try {
      onChangeLoading(true);
      const token = await dispatch(renewToken());
      const response = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("connectGoogleAds")}/connectGoogleAds`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          themeID: THEME_ID_PROJECT,
          redirectUrl: getRedirectUrl(),
        },
      });
      onChangeLoading(false);
      if (response.data.ok) {
        popup.location.href = response.data.url;
        onChangePopupReference(popup);
        return;
      }

      popup.close();
      return false;
    } catch (error) {
      onChangeLoading(false);
      popup.close();
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startConnectGoogleAds",
        })
      );
      return false;
    }
  };

export const startResolveOAuthPlatform =
  ({ state }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("resolveOAuthPlatform")}/resolveOAuthPlatform`,
        data: { state },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startResolveOAuthPlatform",
        })
      );
      return { ok: false };
    }
  };

export const startGetTokenGoogleAds = (data) => async (dispatch, getState) => {
  try {
    const token = await dispatch(renewToken());

    const googleIntegrations = getState()?.user?.integrations?.google;

    const { data: result } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("getTokenGoogleAds")}/getTokenGoogleAds`,
      data: {
        ...data,
        themeID: THEME_ID_PROJECT,
        redirectUrl: getRedirectUrl(),
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!result.ok) return { ok: false };

    if (_.isEmpty(googleIntegrations)) {
      dispatch(
        startSetLastSelectedIntegrationDataByPlatform({
          platform: GOOGLE,
          integrationID: result?.data?.id,
          adAccountID: Object.keys(result?.data?.advertisers || {})[0],
        })
      );
    }

    dispatch(getGoogleAdsIntegration(result.data));
    return { ok: true, data: result.data };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetTokenGoogleAds",
      })
    );

    return { ok: false };
  }
};
const getGoogleAdsIntegration = (data) => ({
  type: types.GET_GOOGLE_ADS_INTEGRATION,
  payload: data,
});

export const startGetDriveAccessTokenForPicker =
  ({ integrationID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getDriveAccessTokenForPicker")}/getDriveAccessTokenForPicker`,
        data: {
          integrationID,
          themeID: THEME_ID_PROJECT,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!result.ok) return { ok: false };

      return { ok: true, data: result.data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetDriveAccessTokenForPicker",
        })
      );

      return { ok: false };
    }
  };

export const startSaveGoogleDriveFilesInStorage =
  ({ integrationID, path, files }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("saveGoogleDriveFilesInStorage")}/saveGoogleDriveFilesInStorage`,
        data: {
          integrationID,
          path,
          files,
          themeID: THEME_ID_PROJECT,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!result.ok) return { ok: false };

      return { ok: true, data: result.data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveGoogleDriveFilesInStorage",
        })
      );

      return { ok: false };
    }
  };

export const startRevokeGoogleAdsAccount =
  ({ id }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("revokeGoogleAdsAccount")}/revokeGoogleAdsAccount`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          id,
        },
      });
      if (result.ok) {
        dispatch(revokeGoogleAdsIntegration({ id }));
        await dispatch(
          startClearAdvertiserDefaultPlatformValuesIfMatchingIntegration({
            platform: GOOGLE,
            integrationID: id,
          })
        );
        return true;
      }
      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startRevokeGoogleAdsAccount",
        })
      );
      return false;
    }
  };
const revokeGoogleAdsIntegration = (data) => ({
  type: types.REVOKE_GOOGLE_ADS_INTEGRATION,
  payload: data,
});

export const startConnectLinkedInAds =
  ({ onChangeLoading, onChangePopupReference = () => {} }) =>
  async (dispatch) => {
    const width = 500;
    const height = 700;
    const popup = window.open(
      "about:blank",
      "LinkedIn Auth",
      `width=${width},height=${height}`
    );

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      PopupBlockedGuideAlert();
      return false;
    }

    try {
      onChangeLoading(true);
      const token = await dispatch(renewToken());
      const response = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("connectLinkedInAds")}/connectLinkedInAds`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          themeID: THEME_ID_PROJECT,
          redirectUrl: getRedirectUrl(),
        },
      });
      onChangeLoading(false);
      if (response.data.ok) {
        popup.location.href = response.data.url;
        onChangePopupReference(popup);
        return;
      }

      popup.close();
      return false;
    } catch (error) {
      onChangeLoading(false);
      popup.close();
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startConnectLinkedInAds",
        })
      );
      return false;
    }
  };

export const startGetTokenLinkedInAds =
  (data) => async (dispatch, getState) => {
    try {
      const token = await dispatch(renewToken());

      const linkedInIntegrations = getState()?.user?.integrations?.[LINKEDIN];

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getTokenLinkedInAds")}/getTokenLinkedInAds`,
        data: {
          ...data,
          themeID: THEME_ID_PROJECT,
          redirectUrl: getRedirectUrl(),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!result.ok) return { ok: false };

      if (_.isEmpty(linkedInIntegrations)) {
        dispatch(
          startSetLastSelectedIntegrationDataByPlatform({
            platform: LINKEDIN,
            integrationID: result?.data?.id,
            adAccountID: Object.keys(result?.data?.advertisers || {})[0],
          })
        );
      }

      dispatch(getLinkedInAdsIntegration(result.data));
      return { ok: true, data: result.data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetTokenLinkedInAds",
        })
      );

      return { ok: false };
    }
  };
const getLinkedInAdsIntegration = (data) => ({
  type: types.GET_LINKEDIN_ADS_INTEGRATION,
  payload: data,
});

export const startRevokeLinkedInAdsAccount =
  ({ id }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("revokeLinkedInAdsAccount")}/revokeLinkedInAdsAccount`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          id,
        },
      });
      if (result.ok) {
        dispatch(revokeLinkedInAdsIntegration({ id }));
        await dispatch(
          startClearAdvertiserDefaultPlatformValuesIfMatchingIntegration({
            platform: LINKEDIN,
            integrationID: id,
          })
        );
        return true;
      }
      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startRevokeLinkedInAdsAccount",
        })
      );
      return false;
    }
  };
const revokeLinkedInAdsIntegration = (data) => ({
  type: types.REVOKE_LINKEDIN_ADS_INTEGRATION,
  payload: data,
});

// Conectar Google Drive (inicia el flujo OAuth)
export const startConnectGoogleDrive =
  ({ onChangeLoading, onChangePopupReference = () => {} }) =>
  async (dispatch) => {
    try {
      onChangeLoading(true);

      const token = await dispatch(renewToken());

      const response = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("connectGoogleDrive")}/connectGoogleDrive`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          themeID: THEME_ID_PROJECT,
          redirectUrl: getRedirectUrl(),
        },
      });

      onChangeLoading(false);

      if (response.data.ok) {
        const width = 500;
        const height = 700;
        const title = `Google Drive Auth`;

        const url = response.data.url;

        const popup = window.open(
          url,
          title,
          `width=${width},height=${height}`
        );

        if (!popup || popup.closed || typeof popup.closed == "undefined") {
          SimpleAlert({
            title: i18next.t(WARNING),
            text: i18next.t("ALERT_POPUP_BLOCKED_DESCRIPTION"),
            icon: ALERT_ICON_TYPE_WARNING,
          });
          return;
        }

        onChangePopupReference(popup);

        return;
      }

      return false;
    } catch (error) {
      onChangeLoading(false);
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startConnectGoogleDrive",
        })
      );
      return false;
    }
  };

export const startGetTokenGoogleDrive =
  (data) => async (dispatch, getState) => {
    try {
      const token = await dispatch(renewToken());

      const googleDriveIntegrations =
        getState()?.user?.integrations?.googleDrive;

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getTokenGoogleDrive")}/getTokenGoogleDrive`,
        data: {
          ...data,
          themeID: THEME_ID_PROJECT,
          redirectUrl: getRedirectUrl(),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!result.ok) return { ok: false };

      if (_.isEmpty(googleDriveIntegrations)) {
        dispatch(
          startSetLastSelectedIntegrationDataByPlatform({
            platform: GOOGLE_DRIVE,
            integrationID: result?.data?.id,
          })
        );
      }

      dispatch(getGoogleDriveIntegration(result.data));

      return { ok: true, data: result.data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetTokenGoogleDrive",
        })
      );
      return { ok: false };
    }
  };

// Action creator para guardar la integración en el store
const getGoogleDriveIntegration = (data) => ({
  type: types.GET_GOOGLE_DRIVE_INTEGRATION,
  payload: data,
});

// Revocar cuenta de Google Drive
export const startRevokeGoogleDriveAccount =
  ({ id }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("revokeGoogleDriveAccount")}/revokeGoogleDriveAccount`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          id,
        },
      });

      if (result.ok) {
        dispatch(revokeGoogleDriveIntegration({ id }));

        return true;
      }

      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startRevokeGoogleDriveAccount",
        })
      );
      return false;
    }
  };

// Action creator para revocar la integración del store
const revokeGoogleDriveIntegration = (data) => ({
  type: types.REVOKE_GOOGLE_DRIVE_INTEGRATION,
  payload: data,
});

export const startGetCustomLoginToken =
  ({ hash }) =>
  async (dispatch) => {
    try {
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getCustomLoginToken")}/getCustomLoginToken`,
        data: {
          hash,
        },
      });

      return {
        ok: result.ok,
        data: result.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetCustomLoginToken",
        })
      );
      return {
        ok: false,
      };
    }
  };

/**
 * Exchanges an AppSumo OAuth authorization code for session data.
 *
 * Expected response shape from the backend:
 *   { ok: boolean, data: { licenseKey, isRegistered, customToken?, userID?, agencyID?, status } }
 *
 * The code is single-use; the caller must ensure it is consumed only once
 * (use a ref guard to prevent double-execution in React Strict Mode).
 */
export const startAppSumoCallback =
  ({ code }) =>
  async (dispatch) => {
    try {
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("appSumoCallback")}/appSumoCallback`,
        data: { code },
      });

      return {
        ok: result.ok,
        data: result.data,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startAppSumoCallback",
          context: { hasCode: Boolean(code) },
          tags: { action: "appSumoCallback" },
          level: "error",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const getUserIP = (data) => ({
  type: types.GET_USER_IP,
  payload: data,
});

export const startChangeAdvertiserIDCustomClaim =
  ({ advertiserID }) =>
  async (dispatch) => {
    try {
      const user = auth?.currentUser;
      if (!user) {
        return false;
      }

      // Refresh the token for the request without dispatching AUTH_RENEW_TOKEN:
      // claims still hold the previous advertiserID until the cloud function
      // runs. Spreading old claims into Redux here caused the selector form to
      // snap back to the previous brand until the switch completed.
      const token = await user.getIdToken(true);
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("changeAdvertiserIDCustomClaim")}/changeAdvertiserIDCustomClaim`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          advertiserID,
        },
      });

      if (data.ok) {
        await dispatch(renewToken(true));
        dispatch(changeAdvertiserIDCustomClaim(advertiserID));
        dispatch(globalResetAdvertiserData());
      }

      return data.ok;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startChangeAdvertiserIDCustomClaim",
        })
      );
      return false;
    }
  };

export const startGetBusinessNameByAdvertiserIDs =
  ({ advertiserIDs }) =>
  async (dispatch) => {
    try {
      const queriesAdvertisers = [];
      advertiserIDs.forEach((advertiserID) => {
        queriesAdvertisers.push(
          get(ref(db, `advertisers/${advertiserID}/businessName`))
        );
      });
      const advertisersSnapshot = await Promise.all(queriesAdvertisers);

      let advertisers = {};

      advertisersSnapshot.forEach((advertiser, index) => {
        const advertiserData = advertiser.val();
        advertisers[advertiserIDs[index]] = advertiserData;
      });

      return advertisers;
    } catch (error) {
      console.log(error);
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetBusinessNameByAdvertiserIDs",
        })
      );
      return false;
    }
  };
export const startGetLogoUrlByAdvertiserIDs =
  ({ advertiserIDs }) =>
  async (dispatch) => {
    try {
      const queriesAdvertisers = [];
      advertiserIDs.forEach((advertiserID) => {
        queriesAdvertisers.push(
          get(ref(db, `advertisers/${advertiserID}/logoUrl`))
        );
      });
      const advertisersSnapshot = await Promise.all(queriesAdvertisers);

      let advertisers = {};

      advertisersSnapshot.forEach((advertiser, index) => {
        const advertiserData = advertiser.val();
        advertisers[advertiserIDs[index]] = advertiserData;
      });

      return advertisers;
    } catch (error) {
      console.log(error);
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetLogoUrlByAdvertiserIDs",
        })
      );
      return false;
    }
  };

export const startGetAdvertiserKpiSummary =
  ({ advertiserID }) =>
  async (dispatch) => {
    try {
      if (!advertiserID) {
        return {
          ok: false,
          kpiSummary: null,
          code: "MISSING_PARAMETERS",
        };
      }

      const snapshot = await get(
        ref(db, `advertisers/${advertiserID}/kpiSummary`)
      );

      return {
        ok: true,
        kpiSummary: snapshot.val() ?? null,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetAdvertiserKpiSummary",
          context: { advertiserID },
        })
      );
      return {
        ok: false,
        kpiSummary: null,
      };
    }
  };

export const startGenerateScriptAdVideo = (data) => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());
    const { data: result } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("generateScriptAdVideo")}/generateScriptAdVideo`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data,
    });

    return result;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGenerateScriptAdVideo",
      })
    );
    return { ok: false };
  }
};
const DOWNLOAD_DELAY_MS = 1000;

const OWN_AWS_BUCKETS = [
  import.meta.env.VITE_AWS_BUCKET_NAME,
  import.meta.env.VITE_AWS_BUCKET_NAME_STAGING,
  import.meta.env.VITE_AWS_BUCKET_ORDERS_NAME,
  import.meta.env.VITE_AWS_BUCKET_ORDERS_NAME_STAGING,
];

const isOwnAwsUrl = (url) =>
  OWN_AWS_BUCKETS.some(
    (bucket) => url.includes(bucket) && url.includes("amazonaws")
  );

const resolveCarouselDownloadUrls = (urls) => async (dispatch) => {
  const ownAwsUrls = urls.filter(isOwnAwsUrl);
  if (ownAwsUrls.length === 0) return urls;

  const presignedByOriginal = new Map();

  try {
    const token = await dispatch(renewToken());
    const { data: result } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("generateAwsDownloadUrl")}/generateAwsDownloadUrl`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: { url: ownAwsUrls },
    });

    if (result?.ok) {
      const downloadUrls = result?.data?.url || [];
      ownAwsUrls.forEach((originalUrl, index) => {
        presignedByOriginal.set(
          originalUrl,
          downloadUrls[index] || originalUrl
        );
      });
    } else {
      ownAwsUrls.forEach((url) => presignedByOriginal.set(url, url));
    }
  } catch {
    ownAwsUrls.forEach((url) => presignedByOriginal.set(url, url));
  }

  return urls.map((url) => presignedByOriginal.get(url) || url);
};

export const startGetAwsDownloadUrl = (urlValue) => async (dispatch) => {
  const urls = Array.isArray(urlValue) ? urlValue : [urlValue];
  const ownAwsUrls = urls.filter(isOwnAwsUrl);
  const nonOwnUrls = urls.filter((url) => !isOwnAwsUrl(url));

  const triggerDownload = (downloadUrl) => {
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.click();
  };

  for (let i = 0; i < nonOwnUrls.length; i++) {
    try {
      await downloadContent(nonOwnUrls[i]);
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "triggerDownload",
          capture: "exception",
        })
      );
      console.log(error);
    }
    if (i < nonOwnUrls.length - 1) {
      await new Promise((r) => setTimeout(r, DOWNLOAD_DELAY_MS));
    }
  }

  if (ownAwsUrls.length === 0) return true;

  try {
    const token = await dispatch(renewToken());
    const { data: result } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("generateAwsDownloadUrl")}/generateAwsDownloadUrl`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: { url: ownAwsUrls },
    });
    if (result.ok) {
      const responseData = result?.data || {};
      const downloadUrls = responseData.url || [];
      for (let i = 0; i < downloadUrls.length; i++) {
        triggerDownload(downloadUrls[i]);
        if (i < downloadUrls.length - 1) {
          await new Promise((r) => setTimeout(r, DOWNLOAD_DELAY_MS));
        }
      }
      return true;
    }
    return false;
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "triggerDownload",
        capture: "exception",
      })
    );
    console.log(error);
    for (const url of ownAwsUrls) {
      await downloadContent(url);
      await new Promise((r) => setTimeout(r, DOWNLOAD_DELAY_MS));
    }
    return false;
  }
};

export const startDownloadUrlsAsZip =
  ({ urls, fileName = "download.zip" }) =>
  async (dispatch) => {
    const downloadUrls = (Array.isArray(urls) ? urls : []).filter(Boolean);
    if (downloadUrls.length === 0) return false;

    try {
      const resolvedUrls = await dispatch(
        resolveCarouselDownloadUrls(downloadUrls)
      );
      return await downloadUrlsAsZip({
        urls: resolvedUrls,
        fileName,
      });
    } catch {
      return false;
    }
  };

export const startDownloadCarouselZip =
  ({ urls, fileName = "carousel.zip" }) =>
  async (dispatch) => {
    const carouselUrls = (Array.isArray(urls) ? urls : []).filter(Boolean);
    if (carouselUrls.length === 0) return false;

    try {
      const resolvedUrls = await dispatch(
        resolveCarouselDownloadUrls(carouselUrls)
      );
      return await downloadCarouselZip({
        urls: resolvedUrls,
        fileName,
      });
    } catch {
      return false;
    }
  };

export const startManageUser = (data) => async (dispatch, getState) => {
  try {
    const token = await dispatch(renewToken());
    const agencyID = getState()?.auth?.user?.agencyID;
    const { data: result } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("manageAdsUser")}/manageAdsUser`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {
        ...data,
        password: encryptPassword(data?.password),
        agencyID,
      },
    });

    if (result.ok) {
      if (result?.data?.customToken) {
        await dispatch(reauthenticateWithCustomToken(result.data.customToken));
      }
      dispatch(
        manageUser({ userID: result?.data?.userID, data: result?.data?.data })
      );
    }
    return result.ok;
  } catch (error) {
    const body = error?.response?.data;
    if (body?.code === SEAT_LIMIT_REACHED_CODE) {
      return body;
    }
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startManageUser",
      })
    );
    return false;
  }
};

export const startUpdateAdsUserPassword =
  ({ password }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("updateAdsUserPassword")}/updateAdsUserPassword`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          password: encryptPassword(password),
        },
      });
      if (result.ok && result?.data?.customToken) {
        await dispatch(reauthenticateWithCustomToken(result.data.customToken));
      }
      return result.ok;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUpdateAdsUserPassword",
        })
      );
      return false;
    }
  };

const manageUser = (data) => ({
  type: types.MANAGE_USER,
  payload: data,
});
export const startDeleteUser = (userID) => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());
    const { data: result } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("deleteAdsUser")}/deleteAdsUser`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {
        userID,
      },
    });

    if (result.ok) {
      dispatch(deleteUser(userID));
    }
    return result.ok;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startDeleteUser",
      })
    );
    return false;
  }
};
const deleteUser = (data) => ({
  type: types.DELETE_USER,
  payload: data,
});

export const startSaveMediaInStorage =
  ({ urls, path, metadata }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("saveMediaInStorage")}/saveMediaInStorage`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          urls,
          path,
          ...(metadata && { metadata }),
        },
      });

      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveMediaInStorage",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGenerateImages = (configuration) => async (dispatch) => {
  const alertID = getUniqueID();
  let notificationId = alertID;

  try {
    dispatch(
      addNotification({
        type: "generatingCreative",
        id: alertID,
        status: ALERTS_ASYNC_STATUS.LOADING,
        progress: 0,
        jobType: CREATIVE_JOB_TYPES.generateImages,
        messageKey: getCreativeJobAlertMessageKey({
          jobType: CREATIVE_JOB_TYPES.generateImages,
        }),
        generationData: { ...configuration, isGenerating: true },
      })
    );

    const data = await createCreativeJobAndWaitForResponse({
      dispatch,
      jobType: "generateImages",
      payload: {
        ...configuration,
        imagesUrl: limitReferenceImageUrls({
          imagesUrl: configuration.imagesUrl,
          mediaType: configuration.mediaType,
        }),
      },
      onJobCreated: ({ jobID }) => {
        dispatch(
          promoteCreativeJobNotification({ fromId: notificationId, jobID })
        );
        notificationId = jobID;
      },
    });

    dispatch(
      updateNotification(notificationId, {
        status: ALERTS_ASYNC_STATUS.SUCCESS,
        progress: 100,
        generationData: mapJobResultToGenerationData({
          job: {
            jobType: "generateImages",
            status: CREATIVE_JOB_STATUS.SUCCESS,
            response: data,
          },
          payloadSnapshot: configuration,
        }),
      })
    );

    return data;
  } catch (error) {
    const errorFormatted = await dispatch(
      LaunchError(error, false, [], {
        creativeJobType: CREATIVE_JOB_TYPES.generateImages,
        showBrandUsageLimitAlert: true,
      })
    );
    console.log(errorFormatted);

    dispatch(
      updateNotification(notificationId, {
        status: ALERTS_ASYNC_STATUS.ERROR,
        message: errorFormatted.message,
        progress: 100,
        generationData: {
          ...configuration,
          isGenerating: false,
        },
      })
    );

    return errorFormatted;
  }
};

export const startGenerateVideo = (configuration) => async (dispatch) => {
  const alertID = getUniqueID();
  let notificationId = alertID;

  try {
    dispatch(
      addNotification({
        type: "generatingCreative",
        id: alertID,
        status: ALERTS_ASYNC_STATUS.LOADING,
        progress: 0,
        jobType: CREATIVE_JOB_TYPES.generateVideo,
        messageKey: getCreativeJobAlertMessageKey({
          jobType: CREATIVE_JOB_TYPES.generateVideo,
        }),
        generationData: { ...configuration, isGenerating: true },
      })
    );

    const data = await createCreativeJobAndWaitForResponse({
      dispatch,
      jobType: "generateVideo",
      payload: {
        ...{
          ...configuration,
          imagesUrl: limitReferenceImageUrls({
            imagesUrl: configuration.imagesUrl,
            mediaType: configuration.mediaType,
          }),
        },
        version: "v3",
      },
      onJobCreated: ({ jobID }) => {
        dispatch(
          promoteCreativeJobNotification({ fromId: notificationId, jobID })
        );
        notificationId = jobID;
      },
    });

    dispatch(
      updateNotification(notificationId, {
        status: ALERTS_ASYNC_STATUS.SUCCESS,
        progress: 100,
        generationData: mapJobResultToGenerationData({
          job: {
            jobType: "generateVideo",
            status: CREATIVE_JOB_STATUS.SUCCESS,
            response: data,
          },
          payloadSnapshot: configuration,
        }),
      })
    );

    return data;
  } catch (error) {
    const errorFormatted = await dispatch(
      LaunchError(error, false, [], {
        creativeJobType: CREATIVE_JOB_TYPES.generateVideo,
        showBrandUsageLimitAlert: true,
      })
    );
    console.log(errorFormatted);

    dispatch(
      updateNotification(notificationId, {
        status: ALERTS_ASYNC_STATUS.ERROR,
        message: errorFormatted.message,
        progress: 100,
        generationData: {
          ...configuration,
          isGenerating: false,
        },
      })
    );

    return errorFormatted;
  }
};

export const startAnimateImage =
  ({
    imageUrl,
    aspectRatio = "9:16",
    prompt,
    outroVideoUrl,
    notificationContext,
  } = {}) =>
  async (dispatch) => {
    const trimmedPrompt = typeof prompt === "string" ? prompt.trim() : "";
    const trimmedOutro =
      typeof outroVideoUrl === "string" ? outroVideoUrl.trim() : "";
    const source = notificationContext?.source;
    const generationDataBase = notificationContext?.generationDataBase;
    const modalType =
      source === "modalCreateWithIA"
        ? CREATIVE_ALERT_MODAL_TYPE.createWithIA
        : CREATIVE_ALERT_MODAL_TYPE.gallery;
    const configuration = {
      imageUrl,
      aspectRatio,
      modalType,
      ...(trimmedPrompt ? { prompt: trimmedPrompt } : {}),
      ...(trimmedOutro ? { outroVideoUrl: trimmedOutro } : {}),
    };

    const alertID = getUniqueID();
    let notificationId = alertID;

    if (notificationContext) {
      const initialGenerationData =
        source === "modalCreateWithIA" && generationDataBase
          ? {
              ...generationDataBase,
              isGenerating: generationDataBase.isGenerating ?? true,
              modalType: CREATIVE_ALERT_MODAL_TYPE.createWithIA,
              ...(trimmedPrompt ? { prompt: trimmedPrompt } : {}),
            }
          : {
              modalType: CREATIVE_ALERT_MODAL_TYPE.gallery,
              ...(generationDataBase || {}),
              isGenerating: true,
              ...(trimmedPrompt ? { prompt: trimmedPrompt } : {}),
            };

      dispatch(
        addNotification({
          type: CREATIVE_ALERT_TYPE.generatingCreative,
          id: alertID,
          status: ALERTS_ASYNC_STATUS.LOADING,
          progress: 0,
          jobType: CREATIVE_JOB_TYPES.animateImage,
          messageKey: getCreativeJobAlertMessageKey({
            jobType: CREATIVE_JOB_TYPES.animateImage,
          }),
          source,
          imageUrl,
          generationData: initialGenerationData,
        })
      );
    }

    try {
      const data = await createCreativeJobAndWaitForResponse({
        dispatch,
        jobType: CREATIVE_JOB_TYPES.animateImage,
        payload: configuration,
        onJobCreated: ({ jobID }) => {
          if (!notificationContext) return;
          dispatch(
            promoteCreativeJobNotification({ fromId: notificationId, jobID })
          );
          notificationId = jobID;
        },
      });

      if (notificationContext) {
        dispatch(
          updateNotification(notificationId, {
            status: ALERTS_ASYNC_STATUS.SUCCESS,
            progress: 100,
            source,
            imageUrl,
            resultUrl: data?.data?.url,
            generationData: mapJobResultToGenerationData({
              job: {
                jobType: CREATIVE_JOB_TYPES.animateImage,
                status: CREATIVE_JOB_STATUS.SUCCESS,
                response: data,
              },
              payloadSnapshot: {
                ...configuration,
                ...(generationDataBase || {}),
              },
            }),
          })
        );
      }

      return data;
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, !notificationContext, [], {
          creativeJobType: CREATIVE_JOB_TYPES.animateImage,
        })
      );
      console.log(errorFormatted);
      if (notificationContext) {
        dispatch(
          updateNotification(notificationId, {
            status: ALERTS_ASYNC_STATUS.ERROR,
            progress: 100,
            source,
            imageUrl,
            message: errorFormatted.message,
            generationData: {
              modalType,
              ...(generationDataBase || {}),
              isGenerating: false,
            },
          })
        );
      }

      return errorFormatted;
    }
  };

export const startResizeImage = (configuration) => async (dispatch) => {
  try {
    const normalizedConfig = { ...configuration };
    if (configuration.url && !configuration.urls) {
      normalizedConfig.urls = [configuration.url];
      delete normalizedConfig.url;
    }

    const data = await createCreativeJobAndWaitForResponse({
      dispatch,
      jobType: "resizeImage",
      payload: normalizedConfig,
    });

    return data;
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startResizeImage",
        creativeJobType: CREATIVE_JOB_TYPES.resizeImage,
      })
    );

    return {
      ok: false,
    };
  }
};

export const startResizeImagesBatch = (configuration) => async (dispatch) => {
  const alertID = getUniqueID();
  let notificationId = alertID;
  const {
    items = [],
    formats = [],
    width,
    height,
    persistToContentList = false,
    modalType = "gallery",
    folderID,
    sourceImageCount,
    estimatedCredits,
    generationDataBase,
  } = configuration;

  try {
    dispatch(
      addNotification({
        type: "generatingCreative",
        id: alertID,
        status: ALERTS_ASYNC_STATUS.LOADING,
        progress: 0,
        jobType: CREATIVE_JOB_TYPES.resizeImagesBatch,
        messageKey: getCreativeJobAlertMessageKey({
          jobType: CREATIVE_JOB_TYPES.resizeImagesBatch,
        }),
        generationData: {
          ...(generationDataBase || {}),
          modalType,
          persistToContentList,
          sourceImageCount,
          isGenerating: true,
        },
      })
    );

    const data = await createCreativeJobAndWaitForResponse({
      dispatch,
      jobType: "resizeImagesBatch",
      payload: {
        items: items.map((item) => ({
          selectionId: item.selectionId,
          type: item.type,
          ...(item.type === "carousel"
            ? { urls: item.urls }
            : { url: item.url }),
        })),
        formats,
        ...(width != null ? { width: Number(width) } : {}),
        ...(height != null ? { height: Number(height) } : {}),
        persistToContentList,
        modalType,
        ...(folderID ? { folderID } : {}),
        itemCount: items.length,
        sourceImageCount,
        ...(Number(estimatedCredits) > 0
          ? { estimatedCredits: Number(estimatedCredits) }
          : {}),
      },
      onJobCreated: ({ jobID }) => {
        dispatch(
          promoteCreativeJobNotification({ fromId: notificationId, jobID })
        );
        notificationId = jobID;
      },
    });

    dispatch(
      updateNotification(notificationId, {
        status: ALERTS_ASYNC_STATUS.SUCCESS,
        progress: 100,
        generationData: mapJobResultToGenerationData({
          job: {
            jobType: "resizeImagesBatch",
            status: CREATIVE_JOB_STATUS.SUCCESS,
            response: data,
          },
          payloadSnapshot: {
            ...(generationDataBase || {}),
            modalType,
            persistToContentList,
            sourceImageCount,
          },
        }),
      })
    );

    return data;
  } catch (error) {
    const errorFormatted = dispatch(
      LaunchError(error, false, [], {
        functionName: "startResizeImagesBatch",
        creativeJobType: CREATIVE_JOB_TYPES.resizeImagesBatch,
      })
    );

    dispatch(
      updateNotification(notificationId, {
        status: ALERTS_ASYNC_STATUS.ERROR,
        message: errorFormatted.message,
        progress: 100,
        generationData: {
          ...(generationDataBase || {}),
          modalType,
          sourceImageCount,
          isGenerating: false,
        },
      })
    );

    return {
      ok: false,
      ...errorFormatted,
    };
  }
};

export const startExtractImagesFromUrls =
  ({ url }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("extractImagesFromUrls")}/extractImagesFromUrls`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          url,
        },
      });

      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startExtractImagesFromUrls",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startGetCompetitorsAds =
  ({ platform = META } = {}) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getCompetitorsAds")}/getCompetitorsAds`,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: { platform },
      });

      if (data?.ok === false) {
        return {
          ok: false,
          message: data.message,
          code: data.code,
        };
      }

      const pagesMap =
        data?.ok && data?.data && typeof data.data === "object"
          ? data.data
          : null;

      const items = formatCompetitorAdsItemsForGallery(pagesMap);

      return {
        ok: true,
        data: items,
        ...(data?.errors ? { errors: data.errors } : {}),
        ...(data?.platformErrors
          ? { platformErrors: data.platformErrors }
          : {}),
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetCompetitorsAds",
          tags: { action: "competitorAds" },
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startPersistMetaAdLibraryImageToStorage =
  ({ dataUrl, path }) =>
  async (dispatch) => {
    try {
      return await persistDataUrlToStorage(dispatch, { dataUrl, path });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startPersistMetaAdLibraryImageToStorage",
          tags: { action: "metaAdLibrary" },
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startEditImage = (configuration) => async (dispatch) => {
  try {
    const data = await createCreativeJobAndWaitForResponse({
      dispatch,
      jobType: "editImage",
      payload: {
        url: configuration.url,
        prompts: configuration.prompts,
        businessLookAndFeel: configuration.businessLookAndFeel || "",
        attachmentUrls: Array.isArray(configuration.attachmentUrls)
          ? configuration.attachmentUrls
          : [],
      },
    });

    return data;
  } catch (error) {
    return dispatch(
      LaunchError(error, false, [], {
        functionName: "startEditImage",
        creativeJobType: CREATIVE_JOB_TYPES.editImage,
      })
    );
  }
};

export const startEditImagesBatch = (configuration) => async (dispatch) => {
  try {
    const data = await createCreativeJobAndWaitForResponse({
      dispatch,
      jobType: "editImagesBatch",
      payload: {
        items: configuration.items,
        prompts: configuration.prompts,
        attachmentUrls: Array.isArray(configuration.attachmentUrls)
          ? configuration.attachmentUrls
          : [],
      },
    });

    return data;
  } catch (error) {
    return dispatch(
      LaunchError(error, false, [], {
        functionName: "startEditImagesBatch",
        creativeJobType: CREATIVE_JOB_TYPES.editImagesBatch,
      })
    );
  }
};

const changeAdvertiserIDCustomClaim = (data) => ({
  type: types.CHANGE_ADVERTISER_ID_CUSTOM_CLAIM,
  payload: data,
});

const globalResetAdvertiserData = () => ({
  type: types.GLOBAL_RESET_ADVERTISER_DATA,
});

export const startConnectYouTube =
  ({ onChangeLoading, onChangePopupReference = () => {} }) =>
  async (dispatch) => {
    const width = 500;
    const height = 700;
    const popup = window.open(
      "about:blank",
      "YouTube Auth",
      `width=${width},height=${height}`
    );

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      PopupBlockedGuideAlert();
      return false;
    }

    try {
      onChangeLoading(true);
      const token = await dispatch(renewToken());
      const response = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("connectYouTube")}/connectYouTube`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          themeID: THEME_ID_PROJECT,
          redirectUrl: getRedirectUrl(),
        },
      });
      onChangeLoading(false);
      if (response.data.ok) {
        popup.location.href = response.data.url;
        onChangePopupReference(popup);
        return;
      }

      popup.close();
      return false;
    } catch (error) {
      onChangeLoading(false);
      popup.close();
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startConnectYouTube",
        })
      );
      return false;
    }
  };

export const startGetTokenYouTube = (data) => async (dispatch, getState) => {
  try {
    const token = await dispatch(renewToken());

    const youtubeIntegrations = getState()?.user?.integrations?.youtube;

    const { data: result } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("getTokenYouTube")}/getTokenYouTube`,
      data: {
        ...data,
        themeID: THEME_ID_PROJECT,
        redirectUrl: getRedirectUrl(),
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!result.ok) return { ok: false };

    if (_.isEmpty(youtubeIntegrations)) {
      dispatch(
        startSetLastSelectedIntegrationDataByPlatform({
          platform: YOUTUBE,
          integrationID: result?.data?.id,
          adAccountID: result?.data?.channelID,
        })
      );
    }

    dispatch(getYouTubeIntegration(result.data));
    return { ok: true, data: result.data };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetTokenYouTube",
      })
    );

    return { ok: false };
  }
};

const getYouTubeIntegration = (data) => ({
  type: types.GET_YOUTUBE_INTEGRATION,
  payload: data,
});

export const startRevokeYouTubeAccount =
  ({ id }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("revokeYouTubeAccount")}/revokeYouTubeAccount`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          id,
        },
      });
      if (result.ok) {
        dispatch(revokeYouTubeIntegration({ id }));
        return true;
      }
      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startRevokeYouTubeAccount",
        })
      );
      return false;
    }
  };

const revokeYouTubeIntegration = (data) => ({
  type: types.REVOKE_YOUTUBE_INTEGRATION,
  payload: data,
});

export const startUploadOrganicContent =
  ({
    urls = [],
    language = "",
    description = "",
    scheduledDate = null,
    timezone = null,
    platforms = [],
    platformsData = {},
    isRetry = false,
    scheduleID = null,
    contentIDs = [],
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const payload = {
        platforms,
        platformsData,
      };

      if (Array.isArray(contentIDs) && contentIDs.length > 0) {
        payload.contentIDs = contentIDs;
      }

      const isCalendarRetry = Boolean(isRetry && scheduleID);

      if (!isCalendarRetry || urls.length > 0) {
        payload.urls = urls;
      }
      if (!isCalendarRetry || language !== "") {
        payload.language = language;
      }
      if (!isCalendarRetry || description !== "") {
        payload.description = description;
      }

      if (scheduledDate != null && !isRetry) {
        payload.scheduledDate = scheduledDate;
        if (timezone) {
          payload.timezone = timezone;
        }
      }

      if (isRetry) {
        payload.isRetry = true;
      }

      if (scheduleID) {
        payload.scheduleID = scheduleID;
      }

      console.log(JSON.stringify(payload, null, 2));

      const response = await axios({
        method: "POST",
        url: `${getCloudFunctionUrl("uploadOrganicContent")}/uploadOrganicContent`,
        data: payload,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUploadOrganicContent",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startDeleteScheduledContent =
  ({ id, queue, scheduleID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const response = await axios({
        method: "POST",
        url: `${getCloudFunctionUrl("deleteScheduledContent")}/deleteScheduledContent`,
        data: {
          id,
          queue,
          scheduleID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startDeleteScheduledContent",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startUpdateScheduledContent =
  ({ id, queue, scheduleID, payload: formPayload }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      console.log("🔄 [startUpdateScheduledContent] payload:", formPayload);

      const payload = {
        id,
        queue: queue || "",
        scheduleID,
        payload: {
          ...formPayload,
        },
      };

      const response = await axios({
        method: "POST",
        url: `${getCloudFunctionUrl("updateScheduledContent")}/updateScheduledContent`,
        data: payload,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUpdateScheduledContent",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startRewriteLinkedInPartnershipTextNow =
  ({ text, language }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("rewriteLinkedInPartnershipTextNow")}/rewriteLinkedInPartnershipTextNow`,
        data: {
          text,
          language,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (result?.ok) {
        return result.text;
      }

      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startRewriteLinkedInPartnershipTextNow",
        })
      );
      return false;
    }
  };

export const startPublishLinkedInPartnershipPostNow =
  ({ integrationID, adAccountID, text, language, publishAs }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const payload = {
        integrationID,
        text,
        language,
        publishAs,
      };

      if (publishAs === LINKEDIN_PUBLISH_AS.ORGANIZATION && adAccountID) {
        payload.adAccountID = adAccountID;
      }

      const response = await axios({
        method: "POST",
        url: `${getCloudFunctionUrl("publishLinkedInPartnershipPostNow")}/publishLinkedInPartnershipPostNow`,
        data: payload,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startPublishLinkedInPartnershipPostNow",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startSubmitFeedback =
  ({ action, urls }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!urls || !urls.length) {
        return {
          ok: false,
        };
      }

      const payload = {
        action,
        urls,
      };

      const key = hashStringToID(urls[0]);
      const contentRef = ref(db, `contentGenerationFeedback/${userID}/${key}`);

      if (action === "remove") {
        await remove(contentRef);
      } else {
        await set(contentRef, payload);
      }

      return {
        ok: true,
        key,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSubmitFeedback",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startReportContent =
  ({ reason, urls }) =>
  async (dispatch, getState) => {
    try {
      const token = await dispatch(renewToken());
      const { agencyID, userID: roleUserID } = dispatch(
        getPathDatabaseByRole()
      );
      const authUser = getState()?.auth?.user;
      const advertiserID = authUser?.advertiserID || roleUserID;
      const userID = authUser?.uid;
      const sanitizedUrls = (urls || []).filter(
        (url) => typeof url === "string" && !!url
      );

      if (
        !agencyID ||
        !advertiserID ||
        !userID ||
        !reason?.trim() ||
        !sanitizedUrls.length
      ) {
        return { ok: false };
      }

      const response = await axios({
        method: "POST",
        url: `${getCloudFunctionUrl("reportContent")}/reportContent`,
        data: {
          agencyID,
          advertiserID,
          userID,
          reason: reason.trim(),
          urls: sanitizedUrls,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startReportContent",
          context: {
            hasReason: !!reason?.trim(),
            urlsCount: urls?.length || 0,
          },
        })
      );
      return { ok: false };
    }
  };
